import FilterOutlined from '@ant-design/icons/FilterOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import { Button, Input, List, Popover, Tag, Tooltip, Typography } from 'antd'
import { fuzzyMatch } from 'features/libra/helpers/fuzzyMatch'
import {
  LibraTicketGroup,
  useCreateGroup,
} from 'features/libra/ticket/libraTicketsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoNotifications } from 'features/notifications'
import { Group, X } from 'lucide-react'
import {
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
const ID_UNGROUPED = 'ungrouped'

const StyledListItem = styled(List.Item)<{ $isSelected: boolean }>`
  cursor: pointer;
  padding-left: 0.5rem !important;
  min-width: 24rem;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.background3 : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.background3 : theme.forecast.background2};
  }
`

const StyledTag = styled(Tag)`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`

const { Title } = Typography

const TitleWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FilterContainer = styled.div`
  padding: 0.25rem 0.5rem 0.75rem;
`

const AddGroupContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem 0.5rem 0.25rem;
`

const AddGroupForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 16rem;
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.forecast.activeText};
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const ListHeader = memo(({ onClose }: { onClose: () => void }) => (
  <Title level={5}>
    <HeaderWrapper>
      <TitleWrapper>
        <Group size={16} />
        <span>Select ticket group</span>
      </TitleWrapper>
      <CloseButton onClick={onClose} title='Show all tickets'>
        <X size={16} />
      </CloseButton>
    </HeaderWrapper>
  </Title>
))

ListHeader.displayName = 'ListHeader'

const AddGroupControl = memo(
  ({ onGroupCreated }: { onGroupCreated: (groupId: string) => void }) => {
    const [addOpen, setAddOpen] = useState(false)
    const [newGroupTitle, setNewGroupTitle] = useState('')
    const [isCreatingGroup, setIsCreatingGroup] = useState(false)
    const workflow = useWorkflow()
    const createGroup = useCreateGroup()
    const { errorNotification, successNotification } = useGeckoNotifications()

    const handleCreateGroup = async () => {
      const trimmedTitle = newGroupTitle.trim()
      if (!trimmedTitle) {
        return
      }
      try {
        setIsCreatingGroup(true)
        const createdGroupId = await createGroup({
          title: trimmedTitle,
          organization: workflow.tags.organization,
          site: workflow.tags.site,
          unit: workflow.tags.unit,
        })
        successNotification('Group created successfully')
        onGroupCreated(createdGroupId)
        setNewGroupTitle('')
        setAddOpen(false)
      } catch (error) {
        console.error(error)
        errorNotification(`Failed to create group: ${error}`)
      } finally {
        setIsCreatingGroup(false)
      }
    }

    return (
      <AddGroupContainer>
        <Popover
          content={
            <AddGroupForm>
              <Input
                placeholder='New group name'
                value={newGroupTitle}
                onChange={(event) => setNewGroupTitle(event.target.value)}
                onPressEnter={handleCreateGroup}
                disabled={isCreatingGroup}
              />
              <Button
                type='primary'
                onClick={handleCreateGroup}
                loading={isCreatingGroup}
                disabled={!newGroupTitle.trim() || isCreatingGroup}>
                Create
              </Button>
            </AddGroupForm>
          }
          trigger='click'
          placement='right'
          open={addOpen}
          onOpenChange={setAddOpen}>
          <Button icon={<PlusOutlined />} type='default'></Button>
        </Popover>
      </AddGroupContainer>
    )
  }
)

AddGroupControl.displayName = 'AddGroupControl'

export interface TicketGroupSelectProps {
  groups: ReadonlyArray<LibraTicketGroup>
  /**
   * Selected group ID:
   * - undefined = show all tickets
   * - null = show ungrouped tickets
   * - string = show tickets for specific group
   */
  selectedGroupId: string | null | undefined
  setSelectedGroupId: Dispatch<SetStateAction<string | null | undefined>>
  changeButtonIcon?: ReactNode
  showUngrouped?: boolean
  allowAdd?: boolean
}

export const TicketGroupSelect = memo(
  ({
    groups,
    selectedGroupId,
    setSelectedGroupId,
    changeButtonIcon,
    showUngrouped = true,
    allowAdd = false,
  }: TicketGroupSelectProps) => {
    const [open, setOpen] = useState(false)
    const [filterText, setFilterText] = useState('')

    const handleGroupClick = useCallback(
      (groupId: string | null) => {
        // If clicking on the already selected group, show all tickets (undefined)
        // Otherwise, select the clicked group
        setSelectedGroupId((prev) => (prev === groupId ? undefined : groupId))
      },
      [setSelectedGroupId]
    )

    const listItems = useMemo(
      () => [
        ...groups,
        ...(showUngrouped
          ? [{ id: ID_UNGROUPED, title: 'Ungrouped', tags: {} }]
          : []),
      ],
      [groups, showUngrouped]
    )

    const filteredItems = useMemo(() => {
      const query = filterText.trim().toLowerCase()
      if (!query) {
        return listItems
      }
      return listItems.filter((item) => fuzzyMatch(item.title, query))
    }, [filterText, listItems])

    const handleClose = useCallback(() => {
      setSelectedGroupId(undefined)
    }, [setSelectedGroupId])

    const handleGroupCreated = useCallback(
      (groupId: string) => {
        setSelectedGroupId(groupId)
      },
      [setSelectedGroupId]
    )

    const renderItem = useCallback(
      ({ id, title }: { id: string; title: string }) => (
        <StyledListItem
          $isSelected={
            id === ID_UNGROUPED
              ? selectedGroupId === null
              : id === selectedGroupId
          }
          onClick={() => handleGroupClick(id === ID_UNGROUPED ? null : id)}>
          {title}
        </StyledListItem>
      ),
      [selectedGroupId, handleGroupClick]
    )

    const content = useMemo(
      () => (
        <>
          <ListHeader onClose={handleClose} />
          <FilterContainer>
            <Input
              placeholder='Filter groups'
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              allowClear
              prefix={
                <FilterOutlined
                  style={{ color: CantileverTheme.colors.gray['700'] }}
                />
              }
            />
          </FilterContainer>
          <List dataSource={filteredItems} renderItem={renderItem} />
          {allowAdd && <AddGroupControl onGroupCreated={handleGroupCreated} />}
        </>
      ),
      [
        allowAdd,
        filterText,
        filteredItems,
        handleClose,
        handleGroupCreated,
        renderItem,
      ]
    )

    return (
      <Popover
        content={content}
        trigger='click'
        placement='right'
        open={open}
        onOpenChange={setOpen}>
        <Tooltip
          title='Click to change ticket group'
          open={open ? false : undefined}>
          <StyledTag color={CantileverTheme.colors.SeaGreen}>
            {changeButtonIcon ?? <Group size={14} />}
          </StyledTag>
        </Tooltip>
      </Popover>
    )
  }
)

TicketGroupSelect.displayName = 'TicketGroupSelect'
